import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import {
    History, Search, Eye, X,
    IndianRupee, Calendar, User, Building,
    MapPin, CheckCircle, Smartphone
} from 'lucide-react';
import API_BASE_URL from '../../config/api';

const HistoryWeek = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedWeek, setSelectedWeek] = useState('');
    const [viewUser, setViewUser] = useState(null);
    const [weekInfo, setWeekInfo] = useState({ start: '', end: '' });

    // Generate last 10 Fridays for the filter
    const generateFridays = () => {
        const fridays = [];
        let curr = new Date();
        // Go back to the most recent Friday
        while (curr.getDay() !== 5) {
            curr.setDate(curr.getDate() - 1);
        }

        for (let i = 0; i < 10; i++) {
            fridays.push(new Date(curr).toISOString().split('T')[0]);
            curr.setDate(curr.getDate() - 7);
        }
        return fridays;
    };

    const fridays = generateFridays();

    const fetchHistory = useCallback(async (weekStart = '') => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const url = `${API_BASE_URL}/admin/history_week.php${weekStart ? `?week_start=${weekStart}` : ''}`;
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setData(result.data);
                setWeekInfo(result.week_info);
            }
        } catch (err) {
            console.error('Failed to fetch weekly history', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchHistory(selectedWeek);
    }, [fetchHistory, selectedWeek]);

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const filteredData = data.filter(u =>
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.referral_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <History className="text-indigo-600" />
                            Weekly History Report
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Showing data from <span className="font-semibold text-indigo-600">{formatDate(weekInfo.start)}</span> to <span className="font-semibold text-indigo-600">{formatDate(weekInfo.end)}</span>
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                        <div className="relative flex-1 sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search user..."
                                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="relative sm:w-48">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <select
                                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none appearance-none cursor-pointer shadow-sm font-medium"
                                value={selectedWeek}
                                onChange={(e) => setSelectedWeek(e.target.value)}
                            >
                                <option value="">Current Week</option>
                                {fridays.map(f => (
                                    <option key={f} value={f}>Week of {formatDate(f)}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl shadow-xl shadow-indigo-100/20 border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gradient-to-r from-gray-50 to-white text-gray-500 text-[11px] uppercase tracking-wider font-bold border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4">User Details</th>
                                    <th className="px-6 py-4">Total Earned</th>
                                    <th className="px-6 py-4">Matching</th>
                                    <th className="px-6 py-4">Binary</th>
                                    <th className="px-6 py-4 text-center">Eligible</th>
                                    <th className="px-6 py-4">All Payouts</th>
                                    <th className="px-6 py-4 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent shadow-lg shadow-indigo-200"></div>
                                                <p className="text-gray-400 font-medium animate-pulse">Calculating weekly data...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-20 text-center text-gray-400 font-medium italic">
                                            No data found for this period.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredData.map((user, idx) => (
                                        <tr key={idx} className="hover:bg-indigo-50/30 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold group-hover:scale-110 transition-transform">
                                                        {user.name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{user.name}</p>
                                                        <p className="text-xs text-gray-500 font-mono tracking-tighter uppercase">{user.referral_code}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-lg font-bold">
                                                    <IndianRupee size={14} />
                                                    {user.total_earned.toLocaleString('en-IN')}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-gray-700 font-medium">
                                                {formatCurrency(user.matching)}
                                            </td>
                                            <td className="px-6 py-4 text-gray-700 font-medium">
                                                {formatCurrency(user.binary)}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                {user.eligible === 'Yes' ? (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-[10px] font-black uppercase">
                                                        <CheckCircle size={10} />
                                                        Eligible
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-400 rounded-full text-[10px] font-black uppercase">
                                                        <X size={10} />
                                                        Locked
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="text-gray-900 font-bold">{formatCurrency(user.all_payout)}</p>
                                                <p className="text-[10px] text-gray-400 uppercase font-bold mt-0.5">Total Withdrawn</p>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button
                                                    onClick={() => setViewUser(user)}
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all font-bold text-xs shadow-md shadow-indigo-100 group-hover:translate-x-[-4px]"
                                                >
                                                    <Eye size={14} />
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* User Detail Popup */}
                {viewUser && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                        <div className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
                            {/* Popup Header */}
                            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 flex justify-between items-center text-white">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-2xl font-black backdrop-blur-md">
                                        {viewUser.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black tracking-tight">{viewUser.name}</h3>
                                        <p className="text-indigo-100 text-xs font-bold font-mono tracking-widest">{viewUser.referral_code}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setViewUser(null)}
                                    className="p-2 bg-black/10 hover:bg-black/20 rounded-xl transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Popup Body */}
                            <div className="p-6 md:p-8 space-y-8 max-h-[80vh] overflow-y-auto custom-scrollbar">
                                {/* Basic Info */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 flex items-center gap-4">
                                        <div className="p-3 bg-white rounded-xl text-indigo-600 shadow-sm"><Smartphone size={20} /></div>
                                        <div>
                                            <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Mobile Number</p>
                                            <p className="font-bold text-gray-900">{viewUser.mobile || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 flex items-center gap-4">
                                        <div className="p-3 bg-white rounded-xl text-indigo-600 shadow-sm"><User size={20} /></div>
                                        <div>
                                            <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Email Address</p>
                                            <p className="font-bold text-gray-900">{viewUser.email || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Bank Details */}
                                <section>
                                    <h4 className="text-sm font-black text-gray-800 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <Building size={16} className="text-indigo-500" />
                                        Bank Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 bg-indigo-50/30 p-6 rounded-2xl border border-indigo-100">
                                        {[
                                            { label: 'Bank Name', value: viewUser.bank_details.bank_name },
                                            { label: 'Branch', value: viewUser.bank_details.branch_name },
                                            { label: 'Account Holder', value: viewUser.bank_details.account_holder },
                                            { label: 'Account No.', value: viewUser.bank_details.account_number },
                                            { label: 'IFSC Code', value: viewUser.bank_details.ifsc },
                                            { label: 'UPI ID', value: viewUser.bank_details.upi }
                                        ].map((item, i) => (
                                            <div key={i}>
                                                <p className="text-[10px] text-indigo-400 font-black uppercase mb-1">{item.label}</p>
                                                <p className="text-gray-900 font-bold">{item.value || 'Not Provided'}</p>
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                {/* Personal & Address Details */}
                                <section>
                                    <h4 className="text-sm font-black text-gray-800 uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <MapPin size={16} className="text-indigo-500" />
                                        Personal & Verification
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 bg-purple-50/30 p-6 rounded-2xl border border-purple-100">
                                        <div>
                                            <p className="text-[10px] text-purple-400 font-black uppercase mb-1">PAN Number</p>
                                            <p className="text-gray-900 font-bold uppercase">{viewUser.personal_details.pan || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-purple-400 font-black uppercase mb-1">Full Address</p>
                                            <p className="text-gray-900 font-bold">{viewUser.personal_details.address || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-purple-400 font-black uppercase mb-1">City / State</p>
                                            <p className="text-gray-900 font-bold">{viewUser.personal_details.city} / {viewUser.personal_details.state}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-purple-400 font-black uppercase mb-1">Pincode</p>
                                            <p className="text-gray-900 font-bold">{viewUser.personal_details.pincode}</p>
                                        </div>
                                    </div>
                                </section>
                            </div>

                            {/* Popup Footer */}
                            <div className="bg-gray-50 p-6 border-t border-gray-100 flex justify-end">
                                <button
                                    onClick={() => setViewUser(null)}
                                    className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-bold text-sm shadow-inner"
                                >
                                    Close Details
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default HistoryWeek;
